import { AfterViewChecked, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
    ApplicationFacade,
    BusPosition,
    DetailedRoute,
    DropOffType,
    ExploreMapRoute,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    PickupType,
    ShapeType,
    TransportMode,
    TripDetailsTime,
    Vehicle
} from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, Subscription } from 'rxjs';
import Env from '@ridango/core/env';
import { AppStartupService, BaseComponent, DisruptionMessage, DisruptionMessageType, LatLng } from '@ridango/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, delay, distinctUntilChanged, map, retryWhen, take, takeUntil, tap } from 'rxjs/operators';
import { IonModalService } from '@ridango/core/lib/services/ion-modal.service';
import { Temporal } from '@js-temporal/polyfill';
import { WebSocketSubject } from 'rxjs/internal-compatibility';
import { webSocket } from 'rxjs/webSocket';
import polyline from '@mapbox/polyline';
import { SmallScreenSheetModalContainerService } from '../../small-screen-sheet-modal-container/small-screen-sheet-modal-container.service';

interface TripDetailsComponentData {
    userDepartStopId: string;
    tripRouteName: string;
    tripRouteNr: string;
    tripRouteColor: string;
    tripRouteTextColor: string | null;
    tripRouteOperatorCode?: string | null;
    tripRouteOperatorName?: string | null;
    tripRouteShape: ShapeType;
    times: TripDetailsTime[];
    isLive?: boolean;
    delay?: number;
    tripRouteMode?: string;
    tripRouteMessages: DisruptionMessage[];
}

@Component({
    selector: 'rwidget-trip-details',
    templateUrl: './trip-details.component.html',
    styleUrls: ['./trip-details.component.scss']
})
export class TripDetailsComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChild('stickyHeader') stickyHeader: TemplateRef<any>;
    private headerSet = false;
    readonly DisruptionMessageType = DisruptionMessageType;
    readonly PickupType = PickupType;
    readonly DropOffType = DropOffType;
    data: TripDetailsComponentData;
    isLoadingTripDetails = true;
    physicalBackButtonSubscription$: Subscription;
    assetsPath$ = this.applicationFacade.assetsPath$;
    TransportMode = TransportMode;
    readonly departureTimeFormat: Intl.DateTimeFormatOptions = { timeStyle: 'short', hour12: false };
    getStopCircleBorderColor$: Observable<{
        'background-color': string | undefined;
        'border-color': string | undefined;
    }>;
    private vehicleWebSocket$: WebSocketSubject<Vehicle[]> | null = null;
    private currentVehicle$ = new BehaviorSubject<Vehicle | null>(null);
    private reconnectInterval = 5000;
    private routePolyline: LatLng[] = [];
    public busOuterBorderStyle$: Observable<{ 'border-color': string }>;
    public busInnerStyle$: Observable<{ 'background-color': string }>;
    private busPosition$ = new BehaviorSubject<BusPosition | null>(null);
    public busVisibilityByLine$: Observable<boolean[]>;
    public busPositionByLine$: Observable<number[]>;
    public lineStyles$: Observable<Array<{ background: string; opacity?: string }>>;
    currentBusPosition: BusPosition | null = null;
    isContrast$ = this.applicationFacade.highContrast$;

    constructor(
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private route: ActivatedRoute,
        private otpService: OpenTripPlannerService,
        private platform: Platform,
        private ionModalService: IonModalService,
        private appStartupService: AppStartupService,
        private router: Router,
        private smallScreenSheetModalContainerService: SmallScreenSheetModalContainerService,
        public applicationFacade: ApplicationFacade,
        public translate: TranslateService,
        @Inject('ENV') public env: Env
    ) {
        super();
    }

    get isCancelled() {
        return this.data?.times.some((item) => item.canceled) ?? false;
    }

    ngOnInit(): void {
        this.connectToVehicleWebSocket();
        this.subscribeTripParams();
    }

    ngAfterViewChecked() {
        if (!this.isLoadingTripDetails && this.stickyHeader && !this.headerSet) {
            this.smallScreenSheetModalContainerService.setHeaderTemplate(this.stickyHeader);
            this.headerSet = true;
        }
        if (this.isLoadingTripDetails && this.headerSet) {
            this.headerSet = false;
        }
    }

    setStopCircleBorderColor() {
        this.getStopCircleBorderColor$ = this.applicationFacade.highContrast$.pipe(
            take(1),
            map((isContrast) => {
                return isContrast
                    ? { 'border-color': 'text-contrast-yellow', 'background-color': 'text-contrast-yellow' }
                    : {
                          'border-color': this.data?.tripRouteColor,
                          'background-color':
                              this.env.APP_NAME === 'vastmanland-app' && this.data?.tripRouteColor
                                  ? this.data?.tripRouteColor
                                  : 'white'
                      };
            })
        );
    }

    handleClose() {
        this.openTripPlannerFacade.clearExploreMapRoute();
        this.router.navigate([this.translate.currentLang, 'explore']);

        const breakpoints = this.ionModalService.getBreakpoints();
        if (breakpoints?.length > 0) {
            this.ionModalService.setCurrentBreakpoint(breakpoints[0]);
        }
    }

    openStopDetails(stopId: string): void {
        if (!stopId) {
            return;
        }

        this.router.navigate([this.translate.currentLang, 'explore', 'stop', stopId]);
    }

    private buildGradient(brakePointPercentages: number[], colors: string[]) {
        const blocks = [];
        for (let i = 0; i < colors.length; i++) {
            blocks.push(`${colors[i]} ${i === 0 ? 0 : brakePointPercentages[i - 1]}%`);
            blocks.push(`${colors[i]} ${brakePointPercentages[i]}%`);
        }
        return `linear-gradient(${blocks.join(', ')})`;
    }

    private subscribeTripParams() {
        this.physicalBackButtonSubscription$ = this.platform.backButton.subscribeWithPriority(1000, async () => {
            this.openTripPlannerFacade.clearExploreMapRoute();
        });

        this.route.queryParamMap.pipe(takeUntil(this.componentDestroyed$)).subscribe((paramMap) => {
            const tripId = paramMap.get('tripId');
            const routeId = paramMap.get('routeId');
            const userDepartStopId = paramMap.get('userDepartStopId');
            const fromTime = Number(paramMap.get('fromTime')) || undefined;

            this.isLoadingTripDetails = true;

            forkJoin([
                this.otpService.getTripTimes(tripId, fromTime),
                this.otpService.getRoute(routeId, fromTime, true)
            ])
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(([tripTimes, tripRoute]) => {
                    if (!tripTimes?.length) {
                        this.isLoadingTripDetails = false;
                        return;
                    }
                    const timesForDetailView: TripDetailsTime[] = tripTimes.map((tt) => ({ ...tt, stop: null }));
                    forkJoin(
                        timesForDetailView.map((time) => this.otpService.getStop(time.stopId, 'stops', null, false))
                    )
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe((stops) => {
                            timesForDetailView.forEach((time: TripDetailsTime, i: number) => (time.stop = stops[i]));
                            const startName = stops[0].name;
                            const endName = stops[stops.length - 1].name;
                            const tripTimesForDisplay = timesForDetailView.map((time: TripDetailsTime) => {
                                const prognosisTime = this.getDepartureTime(time).toPlainTime();
                                const plannedTime = this.getCertainDepartureTime(time.scheduledDeparture).toPlainTime();

                                if (!this.env.TIMEPOINTS_ENABLED) {
                                    const delay = time.departureDelay;
                                    const diffMinutes = Math.floor(Math.abs(delay / 60));
                                    const threshold = this.env.EXPLORE.SCHEDULE_TIME_THRESHOLD_LATE ?? 0;

                                    return {
                                        ...time,
                                        departureTime: prognosisTime,
                                        scheduledTime: plannedTime,
                                        scheduledTimeExceedingAllowedThreshold:
                                            time.realtime && delay != null && diffMinutes >= threshold
                                    };
                                }

                                if (time.timepoint) {
                                    return {
                                        ...time,
                                        departureTime:
                                            time.realtime && time.departureDelay != null ? prognosisTime : plannedTime,
                                        scheduledTime: plannedTime,
                                        scheduledTimeExceedingAllowedThreshold: false
                                    };
                                }

                                const delay = time.departureDelay;
                                if (
                                    time.realtime &&
                                    delay != null &&
                                    this.env?.APPROXIMATE_TIME_THRESHOLD_MINUTES != null
                                ) {
                                    const diffMinutes = Math.floor(Math.abs(delay / 60));
                                    const threshold = this.env.APPROXIMATE_TIME_THRESHOLD_MINUTES;

                                    if (diffMinutes >= threshold) {
                                        return {
                                            ...time,
                                            departureTime: prognosisTime,
                                            scheduledTime: plannedTime,
                                            scheduledTimeExceedingAllowedThreshold: true
                                        };
                                    }
                                }
                                return {
                                    ...time,
                                    departureTime: plannedTime,
                                    scheduledTime: plannedTime,
                                    scheduledTimeExceedingAllowedThreshold: false
                                };
                            });

                            this.data = {
                                userDepartStopId: userDepartStopId,
                                tripRouteName: `${startName} - ${endName}`,
                                tripRouteNr: tripRoute.shortName,
                                tripRouteColor: tripRoute.color,
                                tripRouteTextColor: tripRoute?.icon?.textColor,
                                tripRouteOperatorCode: tripRoute?.operatorCode,
                                tripRouteOperatorName: tripRoute?.operatorName,
                                tripRouteShape: tripRoute?.icon?.shape || ShapeType.ROUNDED,
                                times: tripTimesForDisplay,
                                tripRouteMessages: tripRoute.disruptionMessages,
                                tripRouteMode: timesForDetailView[0]?.mode || tripRoute.mode
                            };
                            this.busOuterBorderStyle$ = this.createBusOuterBorderStyle();
                            this.busInnerStyle$ = this.createBusInnerStyle();
                            this.busVisibilityByLine$ = this.createBusVisibilityByLine();
                            this.busPositionByLine$ = this.createBusPositionByLine();
                            this.lineStyles$ = this.createLineStyles();

                            const stop = this.data.times.find(
                                (stop: TripDetailsTime) => stop.stopId === this.data.userDepartStopId
                            );

                            // If no stop found (clicked from bus on map), use first stop with realtime data
                            const stopForStatus = stop || this.data.times.find((time) => time.realtime);

                            this.data.isLive = stopForStatus?.realtime;
                            this.data.delay = stopForStatus?.arrivalDelay;

                            // Generic or Line alerts
                            this.data.tripRouteMessages = this.filterKeepActiveAlerts(
                                tripRoute.disruptionMessages,
                                timesForDetailView
                            );

                            // Stop alerts
                            tripRoute.disruptionMessages = this.filterKeepActiveAlerts(
                                tripRoute.disruptionMessages,
                                timesForDetailView
                            );

                            this.assignDisruptionMessagesToStops(tripRoute);
                            this.setStopCircleBorderColor();

                            if (this.env.DIRECTIONAL_ALERTS) {
                                this.removeNonDirectionalStopAlerts();
                                this.removeNonDirectionalTripAlerts();
                            }

                            this.addTripToMap(tripId, userDepartStopId, tripRoute, timesForDetailView);
                        })
                        .add(() => (this.isLoadingTripDetails = false));
                });
        });
    }

    // Filter out alerts that are not active during the trip or not active during the departing from stop
    private filterKeepActiveAlerts(messages: DisruptionMessage[], tripTimes: TripDetailsTime[]): DisruptionMessage[] {
        return messages.filter((message) => {
            const { activePeriodStart, activePeriodEnd } = message;
            const start = new Date(activePeriodStart).getTime();
            const end = new Date(activePeriodEnd).getTime();

            const isStopAlert = message.messageType === DisruptionMessageType.ALERT_STOP;

            let lowerBound: number | undefined;
            let upperBound: number | undefined;

            if (isStopAlert) {
                tripTimes.forEach((time) => {
                    message.informedEntities.forEach((e) => {
                        if (e.stopId === time.stopId) {
                            lowerBound = new Date(time.scheduledDeparture).getTime();
                            upperBound = new Date(time.scheduledArrival).getTime();
                        }
                    });
                });
            } else {
                const firstTime = tripTimes[0];
                const lastTime = tripTimes[tripTimes.length - 1];
                if (firstTime?.scheduledDeparture && lastTime?.scheduledArrival) {
                    lowerBound = new Date(firstTime.scheduledDeparture).getTime();
                    upperBound = new Date(lastTime.scheduledArrival).getTime();
                }
            }

            if (!lowerBound || !upperBound) {
                return false;
            }

            return end >= lowerBound && start <= upperBound;
        });
    }

    /**
     * Assigns disruption messages to relevant stops by matching stop IDs and names.
     * Ensures that disruptions not already present on a stop are added.
     */
    private assignDisruptionMessagesToStops(tripRoute: any): void {
        if (tripRoute.disruptionMessages && tripRoute.disruptionMessages.length > 0) {
            this.data.times.forEach((time) => {
                const matchingDisruptions = tripRoute.disruptionMessages.filter((disruption) => {
                    if (disruption.informedEntities && disruption.informedEntities.length > 0) {
                        return disruption.informedEntities.some((entity) => {
                            return (
                                entity.stopId === time.stopId &&
                                entity.stopName === time.stop.name &&
                                entity.routeId?.toString() === tripRoute.id
                            );
                        });
                    } else {
                        return false;
                    }
                });

                if (matchingDisruptions.length > 0) {
                    if (!time.stop.disruptionMessages) {
                        time.stop.disruptionMessages = [];
                    }
                    const existingAlertIds = time.stop.disruptionMessages.map((disruption) => disruption.id);

                    matchingDisruptions.forEach((disruption) => {
                        if (!existingAlertIds.includes(disruption.id)) {
                            time.stop.disruptionMessages.push(disruption);
                        }
                    });
                }
            });
        }
    }

    private removeNonDirectionalStopAlerts(): void {
        this.data.times.forEach((time) => {
            if (time.stop.disruptionMessages) {
                time.stop.disruptionMessages = this.filterDirectionalAlerts(
                    time.stop.disruptionMessages,
                    time.tripDirection?.toString(),
                    this.data.tripRouteNr?.toString(),
                    [DisruptionMessageType.ALERT_STOP, DisruptionMessageType.ALERT_BUS_AND_STOP]
                );
            }
        });
    }

    private removeNonDirectionalTripAlerts(): void {
        this.data.tripRouteMessages = this.filterDirectionalAlerts(
            this.data.tripRouteMessages,
            this.data.times[0]?.tripDirection?.toString(),
            this.data.tripRouteNr?.toString(),
            [DisruptionMessageType.ALERT_BUS, DisruptionMessageType.ALERT_BUS_AND_STOP]
        );
    }

    /** Filter out alerts that are not relevant to the trip direction
     *  only applies if setting DIRECTIONAL_ALERTS is set to true in the environment and
     *  the alert has a tripDirection property set
     * */
    private filterDirectionalAlerts(
        messages: DisruptionMessage[],
        tripDirection: string | null,
        routeShortName: string | null,
        messageTypes: DisruptionMessageType[]
    ): DisruptionMessage[] {
        return messages
            .map((message) => {
                const filteredEntities = message.informedEntities?.filter((entity) => {
                    const alertDirection = entity?.tripDirection?.toString();
                    const alertShortName = entity?.routeShortName?.toString();

                    if (!!tripDirection && !!alertDirection) {
                        return (
                            alertDirection === tripDirection &&
                            alertShortName === routeShortName &&
                            messageTypes.includes(message.messageType)
                        );
                    }
                    return true;
                });

                return filteredEntities?.length ? { ...message, informedEntities: filteredEntities } : null;
            })
            .filter((message) => message !== null) as DisruptionMessage[];
    }

    private addTripToMap(tripId: string, userDepartStopId: string, route: DetailedRoute, tripTimes: TripDetailsTime[]) {
        forkJoin([this.otpService.getTripGeometry(tripId)])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([geometry]) => {
                this.routePolyline = this.decodePolyline(geometry.points);

                const exploreMapRoute: ExploreMapRoute = {
                    polyline: geometry.points,
                    polylineColor: route.color,
                    stops: this.otpService.transformTripDetailsTimeToStopMarkers(tripTimes),
                    userDepartureBusStop: tripTimes.find((time) => time.stopId === userDepartStopId)?.stop
                };
                this.openTripPlannerFacade.setExploreMapRoute(exploreMapRoute);
            });
    }

    private connectToVehicleWebSocket(): void {
        if (!this.env.WMB?.WMB_PUBLIC_API || !this.env.WMB?.TENANT_ID) {
            return;
        }

        const baseUrl = this.env.WMB?.WMB_PUBLIC_API;
        const wsUrl = baseUrl.replace('https://', 'wss://') + `/ws/tenant/${this.env.WMB.TENANT_ID}/vehicle/locations`;

        this.vehicleWebSocket$ = webSocket({
            url: wsUrl,
            closeObserver: {
                next: () => {
                    this.currentVehicle$.next(null);
                }
            }
        });

        this.vehicleWebSocket$
            .pipe(
                retryWhen((errors) => errors.pipe(delay(this.reconnectInterval))),
                catchError((error) => {
                    console.error('Error parsing vehicle data:', error);
                    this.currentVehicle$.next(null);
                    return EMPTY;
                }),
                map((vehicles: Vehicle[]) => {
                    const tripId = this.route.snapshot.queryParamMap.get('tripId');
                    return vehicles.find((v: Vehicle) => v.trip?.tripId === tripId) || null;
                }),
                distinctUntilChanged((prev, curr) => {
                    if (!prev && !curr) return true;
                    if (!prev || !curr) return false;
                    return !this.hasVehicleMovedSignificantly(prev, curr);
                }),
                tap((vehicle) => {
                    if (vehicle) {
                        this.calculateBusPositionOnRoute(vehicle.position.latitude, vehicle.position.longitude);
                    }
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((vehicle: Vehicle | null) => {
                this.currentVehicle$.next(vehicle);

                setTimeout(() => {
                    this.setStopCircleBorderColor();
                }, 0);
            });
    }

    private hasVehicleMovedSignificantly(oldVehicle: Vehicle, newVehicle: Vehicle): boolean {
        const threshold = 0.0005;
        const latDiff = Math.abs(oldVehicle.position.latitude - newVehicle.position.latitude);
        const lonDiff = Math.abs(oldVehicle.position.longitude - newVehicle.position.longitude);

        return latDiff > threshold || lonDiff > threshold;
    }

    private hasStopBeenPassed(time: TripDetailsTime): boolean {
        if (!this.currentBusPosition || !this.data?.times) {
            // Fallback to time-based when no GPS
            return this.hasTimePassedStop(time);
        }

        const stopIndex = this.data.times.findIndex((t) => t.stopId === time.stopId);
        if (stopIndex === -1) return false;

        if (this.currentBusPosition.segmentIndex > stopIndex) {
            return true;
        }

        if (this.currentBusPosition.segmentIndex === stopIndex && this.currentBusPosition.progressPercent > 2) {
            return true;
        }

        return false;
    }

    private hasTimePassedStop(time: TripDetailsTime): boolean {
        const now = Temporal.Now.instant();
        const departureTime = time.realtimeDeparture || time.scheduledDeparture;

        if (!departureTime) return false;

        try {
            const departureInstant =
                typeof departureTime === 'number'
                    ? Temporal.Instant.fromEpochSeconds(departureTime)
                    : Temporal.Instant.from(departureTime);

            const bufferSeconds = 120;
            const departureWithBuffer = departureInstant.add({ seconds: bufferSeconds });

            return departureWithBuffer.epochMilliseconds < now.epochMilliseconds;
        } catch (error) {
            console.warn('Error parsing departure time:', departureTime, error);
            return false;
        }
    }

    private calculateBusPositionOnRoute(vehicleLat: number, vehicleLon: number): void {
        if (!this.data?.times || this.data.times.length < 2 || !this.routePolyline.length) {
            this.currentBusPosition = null;
            return;
        }

        let minDistance = Infinity;
        let closestPolylineIndex = 0;

        for (let i = 0; i < this.routePolyline.length; i++) {
            const distance = this.calculateDistance(
                vehicleLat,
                vehicleLon,
                this.routePolyline[i].lat,
                this.routePolyline[i].lng
            );

            if (distance < minDistance) {
                minDistance = distance;
                closestPolylineIndex = i;
            }
        }

        if (minDistance > 0.1) {
            return;
        }

        const segmentInfo = this.findStopSegmentFromPolylineIndex(closestPolylineIndex);
        if (segmentInfo) {
            this.currentBusPosition = {
                segmentIndex: segmentInfo.segmentIndex,
                progressPercent: segmentInfo.progressPercent,
                isAtStop: segmentInfo.isAtStop,
                atStopIndex: segmentInfo.atStopIndex
            };
            this.busPosition$.next(this.currentBusPosition);
        } else {
            this.currentBusPosition = null;
            this.busPosition$.next(null);
        }
    }

    private findStopSegmentFromPolylineIndex(polylineIndex: number): BusPosition | null {
        if (!this.data?.times || this.data.times.length < 2) return null;

        const polylinePoint = this.routePolyline[polylineIndex];
        let minDistance = Infinity;
        let closestStopIndex = 0;

        for (let i = 0; i < this.data.times.length; i++) {
            const stop = this.data.times[i].stop;
            if (!stop) continue;

            const distance = this.calculateDistance(polylinePoint.lat, polylinePoint.lng, stop.lat, stop.lon);

            if (distance < minDistance) {
                minDistance = distance;
                closestStopIndex = i;
            }
        }

        if (minDistance < 0.02) {
            return {
                segmentIndex: closestStopIndex,
                progressPercent: 0,
                isAtStop: true,
                atStopIndex: closestStopIndex
            };
        }

        if (closestStopIndex === 0) {
            const progress = this.calculateProgressBetweenStops(polylinePoint, 0, 1);
            return {
                segmentIndex: 0,
                progressPercent: progress,
                isAtStop: false,
                atStopIndex: undefined
            };
        } else if (closestStopIndex === this.data.times.length - 1) {
            const progress = this.calculateProgressBetweenStops(polylinePoint, closestStopIndex - 1, closestStopIndex);
            return {
                segmentIndex: closestStopIndex - 1,
                progressPercent: progress,
                isAtStop: false,
                atStopIndex: undefined
            };
        } else {
            const prevProgress = this.calculateProgressBetweenStops(
                polylinePoint,
                closestStopIndex - 1,
                closestStopIndex
            );
            const nextProgress = this.calculateProgressBetweenStops(
                polylinePoint,
                closestStopIndex,
                closestStopIndex + 1
            );

            if (prevProgress >= 0 && prevProgress <= 100) {
                return {
                    segmentIndex: closestStopIndex - 1,
                    progressPercent: prevProgress,
                    isAtStop: false,
                    atStopIndex: undefined
                };
            } else {
                return {
                    segmentIndex: closestStopIndex,
                    progressPercent: nextProgress,
                    isAtStop: false,
                    atStopIndex: undefined
                };
            }
        }
    }

    private decodePolyline(encoded: string): LatLng[] {
        const coordinates = polyline.decode(encoded);
        return coordinates.map(([lat, lng]) => ({
            lat,
            lng
        }));
    }

    private calculateProgressBetweenStops(point: LatLng, stopIndex1: number, stopIndex2: number): number {
        const stop1 = this.data.times[stopIndex1]?.stop;
        const stop2 = this.data.times[stopIndex2]?.stop;

        if (!stop1 || !stop2) return 50;

        const segmentPosition = this.findPositionOnLineSegment(
            point.lat,
            point.lng,
            stop1.lat,
            stop1.lon,
            stop2.lat,
            stop2.lon
        );

        return Math.max(0, Math.min(100, segmentPosition.progress * 100));
    }

    private findPositionOnLineSegment(
        pointLat: number,
        pointLon: number,
        line1Lat: number,
        line1Lon: number,
        line2Lat: number,
        line2Lon: number
    ): { distance: number; progress: number } {
        const A = { x: line1Lat, y: line1Lon };
        const B = { x: line2Lat, y: line2Lon };
        const P = { x: pointLat, y: pointLon };

        const AB = { x: B.x - A.x, y: B.y - A.y };
        const AP = { x: P.x - A.x, y: P.y - A.y };

        const AB_squared = AB.x * AB.x + AB.y * AB.y;
        const AP_dot_AB = AP.x * AB.x + AP.y * AB.y;

        let t = AB_squared === 0 ? 0 : AP_dot_AB / AB_squared;
        t = Math.max(0, Math.min(1, t));

        const closestPoint = {
            x: A.x + t * AB.x,
            y: A.y + t * AB.y
        };

        const distance = this.calculateDistance(pointLat, pointLon, closestPoint.x, closestPoint.y);

        return {
            distance: distance,
            progress: t
        };
    }

    private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371;
        const dLat = this.degreesToRadians(lat2 - lat1);
        const dLon = this.degreesToRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.degreesToRadians(lat1)) *
                Math.cos(this.degreesToRadians(lat2)) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    private degreesToRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    private createLineStyles(): Observable<Array<{ background: string; opacity?: string }>> {
        return combineLatest([this.applicationFacade.highContrast$, this.busPosition$]).pipe(
            map(([isContrast]) => {
                if (!this.data?.times) return [];

                return this.data.times.map((time, index) => {
                    const hasBeenPassed = this.hasStopBeenPassed(time);

                    if (isContrast) {
                        const activeColor = '#ffff00';
                        const passedColor = '#888888';

                        const colorToUse = hasBeenPassed ? passedColor : activeColor;
                        if (index === 0) {
                            return {
                                background: this.buildGradient([50, 100], ['transparent', colorToUse])
                            };
                        } else if (index === this.data.times.length - 1) {
                            return {
                                background: this.buildGradient([50, 100], [colorToUse, 'transparent'])
                            };
                        } else {
                            return { background: colorToUse };
                        }
                    } else {
                        const lineColor = this.data?.tripRouteColor;
                        const opacity = hasBeenPassed ? '0.5' : '1';

                        if (index === 0) {
                            return {
                                background: this.buildGradient([50, 100], ['transparent', lineColor]),
                                opacity
                            };
                        } else if (index === this.data.times.length - 1) {
                            return {
                                background: this.buildGradient([50, 100], [lineColor, 'transparent']),
                                opacity
                            };
                        } else {
                            return { background: lineColor, opacity };
                        }
                    }
                });
            })
        );
    }

    private createBusVisibilityByLine(): Observable<boolean[]> {
        return combineLatest([this.currentVehicle$, this.busPosition$]).pipe(
            map(([vehicle]) => {
                if (!vehicle || !this.data?.times) {
                    return this.data?.times?.map(() => false) || [];
                }

                let lastPassedIndex = -1;
                for (let i = 0; i < this.data.times.length; i++) {
                    if (this.hasStopBeenPassed(this.data.times[i])) {
                        lastPassedIndex = i;
                    } else {
                        break;
                    }
                }

                return this.data.times.map(
                    (_, index) => index === lastPassedIndex && lastPassedIndex < this.data.times.length - 1
                );
            })
        );
    }

    private createBusPositionByLine(): Observable<number[]> {
        return combineLatest([this.currentVehicle$, this.busPosition$]).pipe(
            map(([vehicle, busPosition]) => {
                if (!this.data?.times) return [];

                return this.data.times.map((_, lineIndex) => {
                    if (!vehicle || !busPosition) {
                        return 50;
                    }

                    if (busPosition.segmentIndex === lineIndex) {
                        return busPosition.progressPercent;
                    }

                    if (busPosition.isAtStop) {
                        if (busPosition.atStopIndex === lineIndex) {
                            return 0;
                        } else if (busPosition.atStopIndex === lineIndex + 1) {
                            return 100;
                        }
                    }

                    return 70;
                });
            })
        );
    }

    private createBusOuterBorderStyle(): Observable<any> {
        return combineLatest([this.applicationFacade.highContrast$, this.busPosition$]).pipe(
            map(([isContrast, busPosition]) => {
                const isAtStop = busPosition?.isAtStop || false;
                if (isAtStop) {
                    return { 'border-color': 'transparent' };
                } else {
                    return isContrast ? { 'border-color': '#ffff00' } : { 'border-color': this.data?.tripRouteColor };
                }
            })
        );
    }

    private createBusInnerStyle(): Observable<any> {
        return combineLatest([this.applicationFacade.highContrast$, this.busPosition$]).pipe(
            map(([isContrast]) => {
                return isContrast
                    ? { 'background-color': '#ffff00' }
                    : {
                          'background-color': this.data?.tripRouteColor ? this.data?.tripRouteColor : 'white'
                      };
            })
        );
    }

    ngOnDestroy(): void {
        if (this.vehicleWebSocket$) {
            this.vehicleWebSocket$.complete();
        }

        if (this.busPosition$) {
            this.busPosition$.complete();
        }

        this.physicalBackButtonSubscription$.unsubscribe();
        this.openTripPlannerFacade.clearExploreMapRoute();
        this.smallScreenSheetModalContainerService.setHeaderTemplate(null);
        super.ngOnDestroy();
    }

    private getCertainDepartureTime(time: number | string): Temporal.ZonedDateTime {
        return typeof time === 'number'
            ? Temporal.Instant.fromEpochSeconds(time).toZonedDateTimeISO(this.appStartupService.getTimezone())
            : Temporal.Instant.from(time).toZonedDateTimeISO(this.appStartupService.getTimezone());
    }

    private getDepartureTime(time: TripDetailsTime): Temporal.ZonedDateTime {
        const departure = time.realtimeDeparture ?? time.scheduledDeparture;
        return this.getCertainDepartureTime(departure);
    }

    scheduledTimeExceedingAllowedThreshold(time: TripDetailsTime): boolean {
        if (!time.realtime) return false;

        const plainRealtime = this.getCertainDepartureTime(time.realtimeDeparture);
        const plainScheduledTime = this.getCertainDepartureTime(time.scheduledDeparture);

        return (
            this.minutesToDeparture(plainRealtime) - this.minutesToDeparture(plainScheduledTime) >
                this.env.EXPLORE?.SCHEDULE_TIME_THRESHOLD_LATE ||
            this.minutesToDeparture(plainRealtime) - this.minutesToDeparture(plainScheduledTime) <
                -this.env.EXPLORE?.SCHEDULE_TIME_THRESHOLD_EARLY
        );
    }

    private minutesToDeparture(time: Temporal.ZonedDateTime): number {
        return this.durationUntilDeparture(time).round('minutes').total('minutes');
    }

    private durationUntilDeparture(time: Temporal.ZonedDateTime): Temporal.Duration {
        return Temporal.Now.instant()
            .toZonedDateTimeISO(this.appStartupService.getTimezone())
            .round('minute')
            .until(time);
    }
}
